'use client'

import type { ChainType } from '@polaris/core/models'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/src/components/ui/button'
import {
  useActivateAccount,
  useActivateAccountError,
  useActivateAccountIsMutating,
} from '@/src/hooks/account/use-activate-account'
import { fromTokenAcrossChainsAtom } from '@/src/stores/swap/atoms'
import { walletOptionsAtom } from '@/src/stores/wallets/atoms'
import { cn } from '@/src/utils/styles'
import { WalletState } from '../connect-wallet/wallet-states'
import { GeneratingHotkeyInfoDialog } from '../generating-hotkey-info-dialog'

/** UI for showing when current from chain wallet is being used to generate hotkeys. */
export const GeneratingHotkey = ({ className }: { className?: string }) => {
  const [showExplanation, setShowExplanation] = useState(false)
  const [showTimeout, setShowTimeout] = useState(false)
  const isActivatingAccount = useActivateAccountIsMutating()
  const { isError: isActivatingAccountFailed, error: activatingAccountError } =
    useActivateAccountError()
  const fromChainType = useAtomValue(fromTokenAcrossChainsAtom).data
    ?.requested_asset.chain_type
  const connectedWallets = useAtomValue(walletOptionsAtom).connectedWallets
  const fromWallet = fromChainType
    ? connectedWallets[fromChainType as ChainType]
    : undefined

  const { mutate } = useActivateAccount()

  useEffect(() => {
    if (isActivatingAccount) {
      const timer = setTimeout(() => {
        setShowTimeout(true)
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [isActivatingAccount])

  const activateAccount = useCallback(() => {
    if (fromChainType) {
      mutate({
        chainType: fromChainType,
      })
    }
  }, [fromChainType, mutate])

  if (isActivatingAccountFailed) {
    return (
      <div
        className={cn(
          'bg-background border border-border1 rounded-3xl',
          className,
        )}
      >
        <WalletState
          mode='spin'
          animationKey={fromWallet?.displayName}
          walletLogo={fromWallet?.logoUrl}
          walletName={fromWallet?.displayName}
          title='Wallet not activated'
          desc={
            activatingAccountError?.message ??
            'Something went wrong during your account activation. Retry or contact Support for assistance.'
          }
          state='error'
          actions={
            <Button
              type='button'
              variant='primary'
              size='lg'
              className='w-full'
              onClick={activateAccount}
            >
              Retry
            </Button>
          }
          descContainerClassName='gap-[60px]'
          className='pt-[84px] pb-5'
        />
      </div>
    )
  }

  if (!isActivatingAccount) return null

  return (
    <div
      className={cn(
        'bg-background border border-border1 rounded-3xl pb-3',
        className,
      )}
    >
      <WalletState
        mode='spin'
        animationKey={fromWallet?.displayName}
        walletLogo={fromWallet?.logoUrl}
        walletName={fromWallet?.displayName}
        title='Activate account'
        desc={
          showTimeout
            ? "Can't see your wallet? Check behind this browser window for any wallet popups."
            : `Authorize your account in ${fromWallet?.displayName ?? 'your wallet'} to let Polaris simplify your complex trades.`
        }
        state='connecting'
        actions={
          <Button onClick={() => setShowExplanation(true)}>Learn more</Button>
        }
      />
      <GeneratingHotkeyInfoDialog
        isOpen={showExplanation}
        onClose={() => setShowExplanation(false)}
      />
    </div>
  )
}
